import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Collapse, TextField, InputAdornment, IconButton, Typography, Tooltip
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import MapIcon from '@mui/icons-material/Map';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FolderIcon from '@mui/icons-material/Folder';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BalanceIcon from '@mui/icons-material/Balance';
import PublicIcon from '@mui/icons-material/Public';
import RuleIcon from '@mui/icons-material/Rule';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExploreIcon from '@mui/icons-material/Explore';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import CreateIcon from '@mui/icons-material/Create';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import RadioIcon from '@mui/icons-material/Radio';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SchoolIcon from '@mui/icons-material/School';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InsightsIcon from '@mui/icons-material/Insights';
import TimerIcon from '@mui/icons-material/Timer';

const STORAGE_KEY = 'parlsearch-nav-state';

// One-line descriptions of what each collection contains, surfaced as
// hover/focus tooltips on the sidebar nav (#88). Keyed by route path.
const COLLECTION_DESCRIPTIONS = {
  '/parliamentarians': 'Members and Senators — biographical and service records for everyone who has sat in Federal Parliament.',
  '/electorates': 'Federal electoral divisions, their history and boundaries.',
  '/parties': 'Political parties represented in the Australian Parliament.',
  '/parliaments': 'Each Parliament (1901–present) with its dates and key facts.',
  '/ministries': 'Government ministries and the ministers who served in them.',
  '/shadowministries': 'Opposition shadow ministries and shadow ministers.',
  '/ministryrecords': 'Individual ministerial appointment records.',
  '/shadowministryrecords': 'Individual shadow-ministerial appointment records.',
  '/recordsofservice': 'Parliamentary records of service (offices, committees, ministries) for members and senators.',
  '/house-notice-papers': 'House of Representatives Notice Papers — business listed for each sitting day.',
  '/senate-notice-papers': 'Senate Notice Papers — business listed for each sitting day.',
  '/motions': 'House of Representatives notices of motion.',
  '/bills': 'Bills before the Parliament, their progress and text.',
  '/standingorders': 'House of Representatives Standing Orders (the rules of the House).',
  '/senate-standingorders': 'Senate Standing Orders (the rules of the Senate).',
  '/standingorders/compare': 'Compare any two Standing Orders side by side with a word-level diff (including across chambers).',
  '/calendar': 'Parliamentary sitting calendar and scheduled events.',
  '/house-practice': 'House of Representatives Practice — the authoritative procedural reference.',
  '/hansard': 'Hansard — the full transcript of parliamentary debates (House and Senate).',
  '/questions': 'Questions on Notice — written questions and answers, including estimates.',
  '/senateqon': 'Senate Questions on Notice and their answers.',
  '/houseqiw': 'House Questions in Writing and their answers.',
  '/divisions': 'House of Representatives divisions (recorded votes).',
  '/senate-divisions': 'Senate divisions (recorded votes).',
  '/committees': 'Parliamentary committees, their membership and history.',
  '/committee-reports': 'Reports tabled by parliamentary committees.',
  '/tableddocuments': 'Documents formally tabled in the Parliament.',
  '/petitions': 'Petitions presented to the Parliament.',
  '/constitution': 'The Australian Constitution.',
  '/australiaact': 'The Australia Act 1986.',
  '/senate-journals': 'Senate Journals — the official record of Senate proceedings.',
  '/votes-proceedings': 'House Votes and Proceedings — the official record of House proceedings.',
  '/live-minutes': 'Live Minutes — draft running minutes per chamber per sitting day, with time spent on each business item.',
  '/house-questions': 'House Questions (QT) — Question Time questions with their full caption transcripts (asker, responder, portfolio, and the spoken question + answer).',
  '/house-daily-program': 'House Daily Program — the order of business for each House of Representatives sitting day.',
  '/senate-order-of-business': 'Senate Order of Business ("the Red") — the agenda for each Senate sitting day.',
  '/explore': 'Explore curated statistics and visualisations across the data.',
  '/explore/stats': 'Curated parliamentary statistics and breakdowns.',
  '/electorates/map': 'Interactive map of electorate boundaries over time.',
  '/graph': 'Relationship graph visualiser across parliamentary entities.',
};

// Navigation structure with icons
const NAV_STRUCTURE = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: HomeIcon,
    isStandalone: true
  },
  {
    id: 'people',
    label: 'People',
    icon: PeopleIcon,
    items: [
      { label: 'Parliamentarians', path: '/parliamentarians', icon: PersonIcon },
      { label: 'Electorates', path: '/electorates', icon: MapIcon },
      { label: 'Parties', path: '/parties', icon: GroupsIcon },
      { label: 'Elections', path: '/elections', icon: HowToVoteIcon },
    ]
  },
  {
    id: 'parliament',
    label: 'Parliament',
    icon: AccountBalanceIcon,
    items: [
      { label: 'Parliaments', path: '/parliaments', icon: GavelIcon },
      { label: 'Ministries', path: '/ministries', icon: BusinessIcon },
      { label: 'Shadow Ministries', path: '/shadowministries', icon: BusinessIcon },
    ]
  },
  {
    id: 'chamber-documents',
    label: 'Chamber Documents',
    icon: DescriptionIcon,
    items: [
      { label: 'HoR Notice Papers', path: '/house-notice-papers', icon: ArticleIcon },
      { label: 'Senate Notice Papers', path: '/senate-notice-papers', icon: ArticleIcon },
      { label: 'HoR Notices of Motion', path: '/motions', icon: ArticleIcon },
      { label: 'Senate Notices of Motion', path: '/senate-motions', icon: ArticleIcon, comingSoon: true },
      { label: 'Bills', path: '/bills', icon: ArticleIcon },
      { label: 'HoR Standing Orders', path: '/standingorders', icon: RuleIcon },
      { label: 'Senate Standing Orders', path: '/senate-standingorders', icon: RuleIcon },
      { label: 'Compare Standing Orders', path: '/standingorders/compare', icon: CompareArrowsIcon },
      { label: 'Calendar', path: '/calendar', icon: CalendarMonthIcon },
      { label: 'House Practice', path: '/house-practice', icon: MenuBookIcon },
    ]
  },
  {
    id: 'chamber-records',
    label: 'Chamber Records',
    icon: EventNoteIcon,
    items: [
      { label: 'House Votes and Proceedings', path: '/votes-proceedings', icon: ArticleIcon },
      { label: 'Live Minutes', path: '/live-minutes', icon: TimerIcon },
      { label: 'House Questions (QT)', path: '/house-questions', icon: QuestionAnswerIcon },
      { label: 'Senate Journals', path: '/senate-journals', icon: ArticleIcon },
      { label: 'House Daily Program', path: '/house-daily-program', icon: EventNoteIcon },
      { label: 'Senate Order of Business', path: '/senate-order-of-business', icon: EventNoteIcon },
      { label: 'HoR Divisions', path: '/divisions', icon: HowToVoteIcon },
      { label: 'Senate Divisions', path: '/senate-divisions', icon: HowToVoteIcon },
      { label: 'Hansard', path: '/hansard', icon: RecordVoiceOverIcon },
      { label: 'Tabled Papers', path: '/tableddocuments', icon: FolderIcon },
      { label: 'House Questions in Writing', path: '/houseqiw', icon: QuestionAnswerIcon },
      { label: 'Senate Questions on Notice', path: '/senateqon', icon: QuestionAnswerIcon },
    ]
  },
  {
    id: 'committees',
    label: 'Committees',
    icon: GroupsIcon,
    items: [
      { label: 'Committees', path: '/committees', icon: GroupsIcon },
      { label: 'Committee Reports', path: '/committee-reports', icon: ArticleIcon },
      { label: 'Committee Hansard', path: '/committee-hansard', icon: RecordVoiceOverIcon, comingSoon: true },
      { label: 'Senate Estimates QoN', path: '/questions', icon: QuestionAnswerIcon },
      { label: 'Petitions', path: '/petitions', icon: CreateIcon },
    ]
  },
  {
    id: 'media',
    label: 'Media',
    icon: NewspaperIcon,
    items: [
      { label: 'Press Releases', path: '/press-releases', icon: NewspaperIcon, comingSoon: true },
      { label: 'Newspaper Clippings', path: '/newspaper-clippings', icon: NewspaperIcon, comingSoon: true },
      { label: 'Radio and TV', path: '/radio-tv', icon: RadioIcon, comingSoon: true },
    ]
  },
  {
    id: 'publications',
    label: 'Publications',
    icon: LibraryBooksIcon,
    items: [
      { label: 'Library Publications', path: '/library-publications', icon: LibraryBooksIcon, comingSoon: true },
      { label: 'Parliamentary Papers', path: '/parliamentary-papers', icon: ArticleIcon, comingSoon: true },
      { label: 'Budget Papers', path: '/budget-papers', icon: RequestQuoteIcon, comingSoon: true },
      { label: 'Education', path: '/education', icon: SchoolIcon, comingSoon: true },
    ]
  },
  {
    id: 'reference',
    label: 'Reference',
    icon: MenuBookIcon,
    items: [
      { label: 'Constitution', path: '/constitution', icon: BalanceIcon },
      { label: 'Australia Act', path: '/australiaact', icon: PublicIcon },
      { label: 'Statute of Westminster', path: '/statute-of-westminster', icon: HistoryEduIcon, comingSoon: true },
      { label: '1890s Federal Conventions', path: '/federal-conventions', icon: HistoryEduIcon, comingSoon: true },
    ]
  },
  {
    id: 'statistics',
    label: 'Statistics',
    icon: InsightsIcon,
    items: [
      { label: 'Sitting Patterns', path: '/explore/stats#sitting-patterns', icon: CalendarMonthIcon },
      { label: 'Floor Time', path: '/explore/stats#floor-time', icon: TimerIcon },
      { label: 'Question Time', path: '/explore/stats#question-time', icon: QuestionAnswerIcon },
      { label: 'Parliamentarian Demographics', path: '/explore/stats#parliamentarian-demographics', icon: PeopleIcon },
      { label: 'Parties', path: '/explore/stats#parties', icon: GroupsIcon },
      { label: 'Committees', path: '/explore/stats#committees', icon: GroupsIcon },
      { label: 'Divisions', path: '/explore/stats#divisions', icon: HowToVoteIcon },
      { label: 'Legislation', path: '/explore/stats#legislation', icon: ArticleIcon },
      { label: 'Ministerial Statements', path: '/explore/stats#ministerial-statements', icon: RecordVoiceOverIcon },
      { label: 'Leadership', path: '/explore/stats#leadership', icon: GavelIcon },
    ]
  },
  {
    id: 'explore',
    label: 'Explore',
    icon: ExploreIcon,
    items: [
      { label: 'Relationship Graph', path: '/graph', icon: AccountTreeIcon },
      { label: 'Electorates Map', path: '/electorates/map', icon: MapIcon },
      { label: '3D Perspective Nav', path: '/explore', icon: ViewCarouselIcon },
    ]
  }
];

// Get all paths for a section
const getSectionPaths = (section) => {
  if (section.isStandalone) return [section.path];
  return section.items?.map(item => item.path) || [];
};

// Check if path is in section. Item paths may carry a fragment (e.g.
// /explore/stats#sitting-patterns); strip it so the section still
// matches when the user is sitting on /explore/stats without a hash.
const isPathInSection = (path, section) => {
  const paths = getSectionPaths(section);
  return paths.some(p => {
    if (p === '/') return path === '/';
    const base = p.split('#')[0];
    return path.startsWith(base);
  });
};

// Find section containing path
const findSectionForPath = (path) => {
  for (const section of NAV_STRUCTURE) {
    if (isPathInSection(path, section)) {
      return section.id;
    }
  }
  return null;
};

export default function Sidebar({ onClose, isMobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [openSections, setOpenSections] = useState({});
  const filterInputRef = useRef(null);
  const navRef = useRef(null);

  // Load persisted state and auto-expand section for current path
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setOpenSections(JSON.parse(saved));
      }
    } catch (e) {
      console.warn('Failed to load nav state from localStorage:', e);
    }
  }, []);

  // Auto-expand section containing current path when navigating to a new page
  useEffect(() => {
    const sectionId = findSectionForPath(location.pathname);
    if (sectionId) {
      setOpenSections(prev => {
        // Only auto-expand if this section isn't already open
        if (prev[sectionId]) return prev;
        const next = { [sectionId]: true };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch (e) {
          console.warn('Failed to save nav state:', e);
        }
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Save state to localStorage when it changes
  const saveState = useCallback((newState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    } catch (e) {
      console.warn('Failed to save nav state:', e);
    }
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => {
      // If clicking an already open section, close it
      // Otherwise, close all sections and open only the clicked one
      const isCurrentlyOpen = prev[sectionId];
      const next = isCurrentlyOpen ? {} : { [sectionId]: true };
      saveState(next);
      return next;
    });
  };

  const handleNavigate = (path, comingSoon) => {
    if (comingSoon) return; // Don't navigate for coming soon items
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const isSelected = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Filter logic
  const filterLower = filter.toLowerCase();
  const filteredSections = NAV_STRUCTURE.map(section => {
    if (section.isStandalone) {
      const matches = section.label.toLowerCase().includes(filterLower);
      return matches ? section : null;
    }
    const filteredItems = section.items?.filter(item =>
      item.label.toLowerCase().includes(filterLower)
    );
    if (filteredItems?.length > 0) {
      return { ...section, items: filteredItems };
    }
    // Also show section if section label matches
    if (section.label.toLowerCase().includes(filterLower)) {
      return section;
    }
    return null;
  }).filter(Boolean);

  // When filtering, auto-expand matching sections
  useEffect(() => {
    if (filter) {
      const sectionsToOpen = {};
      filteredSections.forEach(section => {
        if (!section.isStandalone) {
          sectionsToOpen[section.id] = true;
        }
      });
      setOpenSections(prev => ({ ...prev, ...sectionsToOpen }));
    }
  }, [filter, filteredSections]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!navRef.current) return;

    const focusableItems = navRef.current.querySelectorAll(
      'button:not([disabled]), [role="button"]:not([disabled])'
    );
    const currentIndex = Array.from(focusableItems).indexOf(document.activeElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < focusableItems.length - 1) {
          focusableItems[currentIndex + 1].focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          focusableItems[currentIndex - 1].focus();
        }
        break;
      case 'Home':
        e.preventDefault();
        focusableItems[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        focusableItems[focusableItems.length - 1]?.focus();
        break;
      case 'Escape':
        if (isMobile && onClose) {
          onClose();
        }
        break;
      default:
        break;
    }
  }, [isMobile, onClose]);

  const renderNavItem = (item, indent = false) => {
    const Icon = item.icon;
    const selected = isSelected(item.path);
    const comingSoon = item.comingSoon;
    const description = COLLECTION_DESCRIPTIONS[item.path];
    const tooltipTitle = comingSoon
      ? `${description ? description + ' ' : ''}(Coming soon)`
      : (description || '');

    const navItem = (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={selected}
          onClick={() => handleNavigate(item.path, comingSoon)}
          disabled={comingSoon}
          aria-current={selected ? 'page' : undefined}
          sx={{
            py: 1,
            pl: indent ? (selected ? 3.625 : 4) : 2.5,
            pr: 2.5,
            borderLeft: selected ? '3px solid' : 'none',
            borderColor: 'primary.main',
            transition: 'all 0.2s ease',
            opacity: comingSoon ? 0.5 : 1,
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.selected' }
            },
            '&:hover': {
              bgcolor: comingSoon ? 'transparent' : 'action.hover'
            },
            '&.Mui-disabled': {
              opacity: 0.5
            }
          }}
        >
          {Icon && (
            <ListItemIcon sx={{ minWidth: 28, opacity: selected ? 1 : 0.6, transition: 'opacity 0.3s ease' }}>
              <Icon sx={{ fontSize: 18 }} color={selected ? 'primary' : 'inherit'} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{item.label}</span>
                {comingSoon && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.5rem',
                      bgcolor: 'grey.300',
                      color: 'grey.700',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontWeight: 600
                    }}
                  >
                    SOON
                  </Typography>
                )}
              </Box>
            }
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: selected ? 600 : 400,
              letterSpacing: 0,
              color: selected ? 'ledger.accentText' : 'text.secondary',
              transition: 'color 0.3s ease'
            }}
          />
        </ListItemButton>
      </ListItem>
    );

    if (!tooltipTitle) {
      return navItem;
    }

    return (
      <Tooltip
        key={item.path}
        title={tooltipTitle}
        placement="right"
        arrow
        enterDelay={400}
      >
        {navItem}
      </Tooltip>
    );
  };

  const renderSection = (section) => {
    if (section.isStandalone) {
      return renderNavItem(section);
    }

    const Icon = section.icon;
    const isOpen = openSections[section.id];
    const hasActiveChild = section.items?.some(item => isSelected(item.path));

    // Collapse uses unmountOnExit, so the controlled region only exists in
    // the DOM while the section is open — reference it via aria-controls only
    // then so the attribute stays valid (WCAG 4.1.2 / #90).
    const sectionRegionId = `nav-section-${section.id}`;

    return (
      <Box key={section.id} component="li" sx={{ listStyle: 'none' }}>
        <ListItem disablePadding component="div">
          <ListItemButton
            onClick={() => toggleSection(section.id)}
            aria-expanded={isOpen}
            aria-controls={isOpen ? sectionRegionId : undefined}
            sx={{
              py: 1.5,
              px: 2.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {Icon && (
              <ListItemIcon sx={{ minWidth: 28, opacity: hasActiveChild || isOpen ? 1 : 0.6, transition: 'opacity 0.3s ease' }}>
                <Icon sx={{ fontSize: 18 }} color={hasActiveChild ? 'primary' : 'inherit'} />
              </ListItemIcon>
            )}
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                fontSize: '0.845rem',
                fontWeight: 700,
                letterSpacing: 0,
                color: hasActiveChild ? 'ledger.accentText' : 'text.primary',
                transition: 'color 0.3s ease'
              }}
            />
            <Box
              sx={{
                transition: 'transform 0.3s ease',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </Box>
          </ListItemButton>
        </ListItem>
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          id={sectionRegionId}
        >
          <List disablePadding component="ul">
            {section.items?.map(item => renderNavItem(item, true))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      ref={navRef}
      onKeyDown={handleKeyDown}
      aria-label="Main navigation"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.paper'
      }}
    >
      {/* Filter input */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          ref={filterInputRef}
          size="small"
          fullWidth
          placeholder="Filter…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: filter && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setFilter('')}
                  aria-label="Clear filter"
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'action.hover',
              borderRadius: 0,
              fontSize: '0.8125rem',
              fontWeight: 500,
              '& fieldset': {
                borderColor: 'divider'
              },
              '&:hover fieldset': {
                borderColor: 'text.secondary'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 1
              }
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'text.secondary',
              opacity: 0.7
            }
          }}
        />
      </Box>

      {/* Navigation list */}
      <Box sx={{ flex: 1, overflow: 'auto', overscrollBehavior: 'contain', pt: 1 }}>
        <List disablePadding>
          {filteredSections.length > 0 ? (
            filteredSections.map((section) => (
              <React.Fragment key={section.id}>
                {renderSection(section)}
              </React.Fragment>
            ))
          ) : (
            <Box component="li" sx={{ p: 3, textAlign: 'center', listStyle: 'none' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8125rem'
                }}
              >
                No results found
              </Typography>
            </Box>
          )}
        </List>
      </Box>
    </Box>
  );
}
